import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, PriorityBadge, TicketIdBadge, STATUS_OPTIONS, STATUS_LABEL } from "@/components/StatusBadge";
import SLABadge from "@/components/SLABadge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { WinnerTrophy } from "@/components/WinnerTrophy";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Sparkles, ArrowLeft, Lock, Paperclip, Star, GitMerge, Clock, Building2, Phone, MapPin, Cpu, History, FileDown, Users, X, UserPlus, Trash2, PhoneCall, Languages, Zap } from "lucide-react";
import { useT, guessLang } from "@/lib/i18n";
import TaskTypeBadge from "@/components/TaskTypeBadge";
import { toast } from "sonner";

const STAFF = ["agent", "team_leader", "admin", "manager"];

export default function TicketDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { t, lang } = useT();
  const isStaff = STAFF.includes(user?.role);
  const isReporter = (tk) => tk && tk.reporter_id === user?.user_id;

  const internalRef = useRef(null);
  const replyRef = useRef(null);
  const [mentionCtx, setMentionCtx] = useState(null); // {field, query, start, end, coords}
  // Cache of per-comment translations: { [comment_id]: { text, target, loading } }
  const [translations, setTranslations] = useState({});

  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [history, setHistory] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [savedReplies, setSavedReplies] = useState([]);
  const [mySnippets, setMySnippets] = useState([]);
  const [reply, setReply] = useState("");
  const [internalNote, setInternalNote] = useState("");
  useEffect(() => {
    [replyRef.current, internalRef.current].forEach((el) => {
      if (!el) { return; }
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight + 2}px`;
    });
  });
  const [emailCustomer, setEmailCustomer] = useState(true);
  const [nextStatus, setNextStatus] = useState("pending_customer");
  const [timeSpent, setTimeSpent] = useState(0);
  const [drafting, setDrafting] = useState(false);
  const [mergeInto, setMergeInto] = useState("");
  const [csatRating, setCsatRating] = useState(0);
  const [csatComment, setCsatComment] = useState("");
  const [showAudit, setShowAudit] = useState(false);
  const fileRef = useRef(null);

  const loadAll = async () => {
    const t = await api.get(`/tickets/${id}`);
    setTicket(t.data);
    const [c, a] = await Promise.all([
      api.get(`/tickets/${id}/comments`),
      api.get(`/tickets/${id}/attachments`),
    ]);
    setComments(c.data);
    setAttachments(a.data);
    if (STAFF.includes(user?.role)) {
      const h = await api.get(`/tickets/${id}/history`);
      setHistory(h.data);
    }
  };

  useEffect(() => {
    loadAll();
    if (STAFF.includes(user?.role)) {
      api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
      api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
      api.get("/saved-replies").then((r) => setSavedReplies(r.data)).catch(() => {});
      api.get("/snippets").then((r) => setMySnippets(r.data)).catch(() => {});
    } else {
      api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    }
  }, [id, user?.role]);

  const update = async (patch) => {
    try {
      const { data } = await api.patch(`/tickets/${id}`, patch);
      setTicket(data);
      if (isStaff) {
        const h = await api.get(`/tickets/${id}/history`);
        setHistory(h.data);
      }
      toast.success("Ticket updated");
    } catch { toast.error("Failed to update"); }
  };

  // Macros: one-tap templates. Two flavours:
  //  - Customer-facing (Solve / Pending / Follow-up) prefill the PUBLIC reply
  //    textarea + tick the "email customer" box.
  //  - Internal-task (Start / Progress / Complete / Blocked) prefill the
  //    INTERNAL note textarea + set the next status. No customer email.
  // Both flavours only fill state; nothing sends until the agent hits Post.
  const MACROS = {
    // Customer flow (unchanged)
    solve: {
      field: "reply", email: true, status: "resolved",
      text:
        `Hi ${ticket?.reporter_name || "there"},\n\n` +
        `We&#39;ve resolved your ticket. If the issue reappears, just reply to this email and we&#39;ll reopen it immediately.\n\n` +
        `Thanks for working with the KINOS IT team.`,
    },
    pending: {
      field: "reply", email: true, status: "pending_customer",
      text:
        `Hi ${ticket?.reporter_name || "there"},\n\n` +
        `We need a little more information to move forward. Could you please reply with:\n` +
        ` &bull; the exact time the issue started\n` +
        ` &bull; a screenshot or short description of what you see on screen\n\n` +
        `Once we have that, we&#39;ll pick it right back up.`,
    },
    follow: {
      field: "reply", email: true, status: "in_progress",
      text:
        `Hi ${ticket?.reporter_name || "there"},\n\n` +
        `A quick follow-up on your ticket &mdash; we&#39;re still working on it and will update you again shortly with progress or next steps.\n\n` +
        `Thanks for your patience.`,
    },
    // Internal-task flow (writes to internal note, no customer email)
    int_start: {
      field: "internal", email: false, status: "in_progress",
      text:
        `Started work on this internal task.\n\n` +
        `Owner: ${user?.name || "me"}\n` +
        `Planned steps:\n` +
        ` &bull; [step 1]\n` +
        ` &bull; [step 2]\n` +
        ` &bull; [step 3]`,
    },
    int_progress: {
      field: "internal", email: false, status: "in_progress",
      text:
        `Progress update &mdash; ${new Date().toLocaleString()}\n\n` +
        `Done today:\n` +
        ` &bull; [what was completed]\n\n` +
        `Next up:\n` +
        ` &bull; [what&#39;s next]`,
    },
    int_complete: {
      field: "internal", email: false, status: "resolved",
      text:
        `Task complete.\n\n` +
        `Outcome:\n` +
        ` &bull; [summary of what was delivered]\n\n` +
        `Handoff / attachments:\n` +
        ` &bull; [links or files, if any]`,
    },
    int_blocked: {
      field: "internal", email: false, status: "pending_third_party",
      text:
        `Task blocked &mdash; awaiting external input.\n\n` +
        `Blocked on:\n` +
        ` &bull; [person / vendor / dependency]\n\n` +
        `Requested from them:\n` +
        ` &bull; [what we need to unblock]`,
    },
  };
  const applyMacro = (name) => {
    const m = MACROS[name];
    if (!m) return;
    // Decode &#39; / &mdash; / &bull; since we&#39;re writing to a textarea.
    const decoded = m.text
      .replace(/&#39;/g, "'")
      .replace(/&mdash;/g, "\u2014")
      .replace(/&bull;/g, "\u2022");
    if (m.field === "internal") {
      setInternalNote(decoded);
      setEmailCustomer(false);
      setTimeout(() => internalRef.current?.focus(), 30);
    } else {
      setReply(decoded);
      setEmailCustomer(!!m.email);
      setTimeout(() => replyRef.current?.focus(), 30);
    }
    setNextStatus(m.status);
    const nice = name.replace(/^int_/, "").replace(/^./, (c) => c.toUpperCase());
    toast.success(`Macro applied: ${nice}`);
  };

  const submitReply = async () => {
    const hasPublic = reply.trim().length > 0;
    const hasInternal = internalNote.trim().length > 0;
    if (!hasPublic && !hasInternal) {
      toast.error("Write a public reply, an internal note, or both");
      return;
    }
    try {
      const time = Number(timeSpent) || 0;
      const results = [];
      // Post internal note first (team log), no email
      if (hasInternal && isStaff) {
        const r = await api.post(`/tickets/${id}/comments`, {
          content: internalNote,
          is_internal: true,
          time_spent_min: hasPublic ? 0 : time,
          send_email: false,
        });
        results.push({ kind: "internal", data: r.data });
      }
      // Post public reply, optionally emailing the customer, with optional status transition
      if (hasPublic) {
        const r = await api.post(`/tickets/${id}/comments`, {
          content: reply,
          is_internal: false,
          time_spent_min: time,
          send_email: emailCustomer,
          next_status: nextStatus === "__keep__" ? null : nextStatus,
        });
        results.push({ kind: "public", data: r.data });
      }
      // Feedback toasts
      if (hasInternal && hasPublic) {
        toast.success("Internal note logged and public reply posted");
      } else if (hasInternal) {
        toast.success("Internal note added");
      } else {
        toast.success("Reply posted");
      }
      if (hasPublic && emailCustomer) {
        const publicResult = results.find((x) => x.kind === "public")?.data;
        if (publicResult?.email_sent) toast.success(`Emailed ${ticket.reporter_email}`);
        else toast.warning("Reply posted, but email could not be sent.");
      }
      setReply(""); setInternalNote(""); setEmailCustomer(true); setTimeSpent(0); setNextStatus("pending_customer");
      await loadAll();
    } catch { toast.error("Failed to post"); }
  };

  const draft = async () => {
    setDrafting(true);
    try {
      const { data } = await api.post(`/tickets/${id}/ai-draft`);
      let i = 0; const full = data.draft;
      setReply("");
      const step = () => {
        i += 4;
        setReply(full.slice(0, i));
        if (i < full.length) setTimeout(step, 12);
      };
      step();
    } catch { toast.error("AI draft failed"); }
    finally { setDrafting(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/tickets/${id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const a = await api.get(`/tickets/${id}/attachments`);
      setAttachments(a.data);
      toast.success(`${file.name} uploaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  };

  const submitCsat = async () => {
    if (!csatRating) { toast.error("Pick a rating"); return; }
    try {
      await api.post(`/tickets/${id}/csat`, { rating: csatRating, comment: csatComment || null });
      toast.success("Thanks for your feedback!");
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const doMerge = async () => {
    if (!mergeInto) return;
    try {
      const { data: target } = await api.get(`/tickets/${mergeInto}`);
      if (!target) { toast.error("Target not found"); return; }
      await api.post(`/tickets/${id}/merge`, { into_ticket_id: mergeInto });
      toast.success(`Merged into ${target.ticket_number}`);
      nav(`/tickets/${mergeInto}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Merge failed"); }
  };

  const currentCat = useMemo(() => categories.find((c) => c.category_id === ticket?.category_id), [ticket, categories]);

  const addCollaborator = async (userId) => {
    if (!userId) return;
    try {
      const { data } = await api.post(`/tickets/${id}/collaborators`, { user_id: userId });
      setTicket(data);
      const h = await api.get(`/tickets/${id}/history`);
      setHistory(h.data);
      const u = users.find((x) => x.user_id === userId);
      toast.success(`Shared with ${u?.name || "colleague"}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to share"); }
  };

  const removeCollaborator = async (userId) => {
    try {
      const { data } = await api.delete(`/tickets/${id}/collaborators/${userId}`);
      setTicket(data);
      const h = await api.get(`/tickets/${id}/history`);
      setHistory(h.data);
      toast.success("Collaborator removed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const deleteTicket = async () => {
    try {
      await api.delete(`/tickets/${id}`);
      const num = ticket?.ticket_number;
      nav("/tickets");
      toast.success(`Deleted ${num}`, {
        duration: 10000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post(`/tickets/${id}/restore`);
              toast.success(`${num} restored`);
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Restore failed");
            }
          },
        },
      });
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  // ---- @Mention autocomplete ----
  const detectMention = (textarea, field) => {
    if (!textarea) return null;
    const caret = textarea.selectionStart ?? 0;
    const before = (textarea.value || "").slice(0, caret);
    const m = before.match(/(?:^|\s)@([A-Za-z][A-Za-z0-9._\-]{0,40})$/);
    if (!m) return null;
    const query = m[1] || "";
    const start = caret - m[1].length - 1; // include '@'
    return { field, query, start, end: caret };
  };
  const onComposerInput = (field) => (e) => {
    const setter = field === "internal" ? setInternalNote : setReply;
    setter(e.target.value);
    const ctx = detectMention(e.target, field);
    setMentionCtx(ctx);
  };
  const insertMention = (name) => {
    if (!mentionCtx) return;
    const first = (name || "").split(" ")[0].replace(/[^A-Za-z0-9._\-]/g, "");
    const value = mentionCtx.field === "internal" ? internalNote : reply;
    const before = value.slice(0, mentionCtx.start);
    const after = value.slice(mentionCtx.end);
    const next = `${before}@${first} ${after}`;
    if (mentionCtx.field === "internal") setInternalNote(next);
    else setReply(next);
    setMentionCtx(null);
    // Return focus with cursor after the inserted mention
    const ref = mentionCtx.field === "internal" ? internalRef : replyRef;
    setTimeout(() => {
      if (ref.current) {
        const pos = mentionCtx.start + first.length + 2;
        ref.current.focus();
        ref.current.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  if (!ticket) return <div className="text-slate-500 font-mono text-xs uppercase tracking-[0.3em]">Loading ticket...</div>;

  const agents = users.filter((u) => STAFF.includes(u.role));
  const collabIds = ticket.collaborators || [];
  const collabNames = ticket.collaborator_names || [];
  const shareCandidates = agents.filter(
    (a) => a.user_id !== ticket.assignee_id && !collabIds.includes(a.user_id)
  );
  const canEditReporterFields = isStaff;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Link to="/tickets" className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-slate-900 font-mono uppercase tracking-[0.2em]" data-testid="back-to-tickets">
          <ArrowLeft className="h-3 w-3" strokeWidth={1.5} /> All Tickets
        </Link>
        <div className="flex items-center gap-2">
          {(ticket.status === "resolved" || ticket.status === "closed") && (
            <a href={`${API_BASE}/tickets/${ticket.ticket_id}/report.pdf`} target="_blank" rel="noreferrer" data-testid="download-report-btn">
              <Button variant="outline" size="sm" className="border-slate-200 bg-transparent hover:bg-slate-100 rounded-sm text-xs">
                <FileDown className="h-3 w-3" strokeWidth={1.5} /> Service report PDF
              </Button>
            </a>
          )}
          {ticket.duplicate_of && (
            <div className="text-xs font-mono text-slate-600">Merged into · <Link className="text-red-600" to={`/tickets/${ticket.duplicate_of}`}>view parent</Link></div>
          )}
          {user?.role === "admin" && (
            <Button
              variant="outline"
              size="sm"
              onClick={deleteTicket}
              className="border-red-500/40 bg-transparent hover:bg-red-500/10 text-red-300 hover:text-red-200 rounded-sm text-xs"
              data-testid="delete-ticket-btn"
              title="Delete this ticket permanently"
            >
              <Trash2 className="h-3 w-3" strokeWidth={1.5} /> Delete
            </Button>
          )}
        </div>
      </div>

      {/* Header */}
      <div className="border border-slate-200 bg-white p-6">
        <div className="flex items-center gap-3 flex-wrap">
          <TicketIdBadge id={ticket.ticket_number} />
          <StatusBadge status={ticket.status} />
          <PriorityBadge priority={ticket.priority} />
          {ticket.ticket_type === "internal" && ticket.internal_task_type && (
            <TaskTypeBadge type={ticket.internal_task_type} />
          )}
          {(ticket.ai_categorized || ticket.ai_prioritized) && (
            <span className="inline-flex items-center gap-1.5 bg-red-500/10 text-red-600 border border-red-500/30 rounded-sm px-2 py-0.5 text-[11px] font-mono uppercase tracking-wider" data-testid="ai-triage-tag">
              <Sparkles className="h-3 w-3" strokeWidth={1.5} /> {t("td_auto_triaged")}
            </span>
          )}
          <SLABadge dueAt={ticket.resolution_due_at} status={ticket.status} responseDueAt={ticket.first_response_due_at} firstResponseAt={ticket.first_response_at} />
        </div>
        <h1 className="font-display text-3xl font-black tracking-tight mt-4" data-testid="ticket-title">{ticket.title}</h1>
        <p className="text-slate-700 text-sm mt-3 whitespace-pre-wrap leading-relaxed">{ticket.description}</p>
        <div className="mt-4 flex items-center gap-4 text-xs text-slate-500 font-mono flex-wrap">
          <span>{t("td_by")} {ticket.reporter_name}</span><span>·</span>
          <span>{new Date(ticket.created_at).toLocaleString()}</span>
          {ticket.time_spent_min > 0 && (<><span>·</span><span>{t("td_min_logged", { n: ticket.time_spent_min })}</span></>)}
        </div>
      </div>

      {/* 3-column workspace */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Prominent "assignment banner" row */}
      </div>

      {/* Follow-up linkage banner */}
      {ticket.parent_ticket_id && (
        <Link to={`/tickets/${ticket.parent_ticket_id}`} className="flex items-center gap-3 px-4 py-3 border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/15 -mt-2" data-testid="parent-link">
          <PhoneCall className="h-4 w-4 text-amber-300" strokeWidth={1.8} />
          <div className="text-xs text-amber-200">
            {t("td_callback_of")} <span className="underline">{t("td_open_original")} &rarr;</span>
          </div>
        </Link>
      )}
      {ticket.followup_ticket_id && (
        <Link to={`/tickets/${ticket.followup_ticket_id}`} className="flex items-center gap-3 px-4 py-3 border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/15 -mt-2" data-testid="followup-link">
          <PhoneCall className="h-4 w-4 text-amber-300" strokeWidth={1.8} />
          <div className="text-xs text-amber-200">
            {t("td_has_callback")} <span className="underline">{t("td_open_callback")} &rarr;</span>
          </div>
        </Link>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 -mt-2 mb-1">
        <div className="border border-slate-200 bg-white p-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">{t("td_requested_by")}</div>
          <div className="flex items-center gap-2">
            <div className="text-sm font-medium truncate" data-testid="banner-reporter">{ticket.reporter_name}</div>
            {ticket.reporter_vip && (
              <span data-testid="banner-vip" className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-[0.2em] px-1.5 py-0.5 border rounded-sm bg-amber-500/15 text-amber-300 border-amber-500/40">
                <Star className="h-2.5 w-2.5 fill-amber-400 text-amber-400" strokeWidth={2} /> VIP
              </span>
            )}
          </div>
          <div className="text-[11px] text-slate-500 truncate">{ticket.reporter_email}</div>
        </div>
        <div className="border border-slate-200 bg-white p-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">{t("td_assigned_engineer")}</div>
          <div className="text-sm font-medium truncate" data-testid="banner-assignee">
            {ticket.assignee_name || <span className="text-slate-500">{t("td_unassigned")}</span>}
          </div>
          <div className="text-[11px] text-slate-500 truncate">{ticket.assignee_id ? t("td_performing_task") : t("td_awaiting_assignment")}</div>
        </div>
        <div className="border border-slate-200 bg-white p-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">{t("td_support_group")}</div>
          <div className="text-sm font-medium truncate" data-testid="banner-group">{ticket.assigned_group || ticket.category_name || t("td_general_it")}</div>
          <div className="text-[11px] text-slate-500 truncate">{ticket.subcategory || "—"}</div>
        </div>
      </div>

      {/* Collaborators strip */}
      {isStaff && (
        <div className="border border-slate-200 bg-white p-4 -mt-1 mb-1" data-testid="collaborators-panel">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-red-600 flex items-center gap-2">
              <Users className="h-3 w-3" strokeWidth={1.5} /> {t("td_collaborators")} · {collabIds.length}
            </div>
            <div className="flex items-center gap-2 flex-wrap flex-1">
              {collabIds.length === 0 && (
                <span className="text-[11px] text-slate-500">No one else shared yet — add colleagues to work on this together.</span>
              )}
              {collabIds.map((uid, i) => (
                <span key={uid} className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/40 text-cyan-200 rounded-sm px-2 py-1 text-[11px]" data-testid={`collab-chip-${uid}`}>
                  <Avatar className="h-5 w-5 rounded-sm"><AvatarFallback className="rounded-sm bg-cyan-900/60 text-[9px] text-cyan-200">{(collabNames[i] || "?").slice(0,2).toUpperCase()}</AvatarFallback></Avatar>
                  <span className="font-medium">{collabNames[i] || uid}</span>
                  <button onClick={() => removeCollaborator(uid)} className="text-red-700 hover:text-red-300 ml-1" title="Remove" data-testid={`collab-remove-${uid}`}>
                    <X className="h-3 w-3" strokeWidth={2} />
                  </button>
                </span>
              ))}
            </div>
            <Select value="" onValueChange={addCollaborator}>
              <SelectTrigger className="w-56 bg-white border-red-500/40 rounded-sm h-9 text-xs" data-testid="add-collaborator-trigger">
                <span className="inline-flex items-center gap-2 text-red-700">
                  <UserPlus className="h-3.5 w-3.5" strokeWidth={1.5} /> Add colleague…
                </span>
              </SelectTrigger>
              <SelectContent>
                {shareCandidates.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-slate-500">No more staff to add.</div>
                ) : shareCandidates.map((a) => (
                  <SelectItem key={a.user_id} value={a.user_id} data-testid={`share-with-${a.user_id}`}>
                    {a.name} · <span className="text-slate-500 uppercase text-[10px] ml-1">{a.role.replace("_", " ")}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* 3-column workspace */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Left: Actions */}
        {isStaff && (
          <div className="lg:col-span-3 space-y-4">
            <div className="border border-slate-200 bg-white p-4 space-y-3">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-1">Actions</div>
              <SB label="Status">
                <Select value={ticket.status} onValueChange={(v) => update({ status: v })}>
                  <SelectTrigger className="bg-white border-slate-200 rounded-sm h-9" data-testid="update-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS_OPTIONS.map((s) => (<SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>))}</SelectContent>
                </Select>
              </SB>
              <SB label="Priority">
                <Select value={ticket.priority} onValueChange={(v) => update({ priority: v })}>
                  <SelectTrigger className="bg-white border-slate-200 rounded-sm h-9" data-testid="update-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem><SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </SB>
              <SB label="Assignee">
                <Select value={ticket.assignee_id || ""} onValueChange={(v) => update({ assignee_id: v })}>
                  <SelectTrigger className="bg-white border-slate-200 rounded-sm h-9" data-testid="update-assignee"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>{agents.map((a) => (<SelectItem key={a.user_id} value={a.user_id}>{a.name}</SelectItem>))}</SelectContent>
                </Select>
              </SB>
              <SB label="Category">
                <Select value={ticket.category_id || ""} onValueChange={(v) => update({ category_id: v })}>
                  <SelectTrigger className="bg-white border-slate-200 rounded-sm h-9" data-testid="update-category"><SelectValue placeholder="Uncategorized" /></SelectTrigger>
                  <SelectContent>{categories.map((c) => (<SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>))}</SelectContent>
                </Select>
              </SB>
              <SB label="Subcategory">
                <Select value={ticket.subcategory || ""} onValueChange={(v) => update({ subcategory: v })} disabled={!currentCat?.subcategories?.length}>
                  <SelectTrigger className="bg-white border-slate-200 rounded-sm h-9" data-testid="update-subcategory"><SelectValue placeholder={currentCat ? "Select" : "—"} /></SelectTrigger>
                  <SelectContent>{(currentCat?.subcategories || []).map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
                </Select>
              </SB>
            </div>

            {/* Merge */}
            <div className="border border-slate-200 bg-white p-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-2 flex items-center gap-2"><GitMerge className="h-3 w-3" /> Merge</div>
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline" className="w-full border-slate-200 bg-transparent hover:bg-slate-100 rounded-sm text-xs" data-testid="open-merge-btn" disabled={!!ticket.duplicate_of}>
                    Merge into parent...
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white border-slate-200 rounded-sm max-w-md">
                  <DialogHeader><DialogTitle>Merge ticket</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div className="text-xs text-slate-600">Enter the ticket ID (e.g. tkt_xxx) or paste the URL of the parent ticket. All comments will be copied over as internal notes.</div>
                    <Input placeholder="tkt_... (parent ticket id)" value={mergeInto} onChange={(e) => setMergeInto(e.target.value.replace(/.*tickets\//, ""))} className="bg-white border-slate-200 rounded-sm" data-testid="merge-target-input" />
                    <div className="flex justify-end">
                      <Button onClick={doMerge} className="bg-red-600 hover:bg-red-500 rounded-sm" data-testid="confirm-merge-btn">Confirm merge</Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* Audit */}
            <div className="border border-slate-200 bg-white p-4">
              <button onClick={() => setShowAudit(!showAudit)} className="w-full flex items-center justify-between text-slate-600 hover:text-slate-900" data-testid="toggle-audit">
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] flex items-center gap-2"><History className="h-3 w-3" /> Audit history</span>
                <span className="text-xs">{showAudit ? "hide" : "show"}</span>
              </button>
              {showAudit && (
                <div className="mt-3 space-y-2 max-h-64 overflow-auto pr-1">
                  {history.length === 0 && <div className="text-xs text-slate-500">No changes yet.</div>}
                  {history.map((h, i) => (
                    <div key={i} className="text-[11px] font-mono border-l border-slate-200 pl-2 text-slate-600">
                      <div className="text-slate-700">{h.actor_name} <span className="text-slate-500">· {h.action}</span></div>
                      <div className="text-slate-500">{new Date(h.created_at).toLocaleString()}</div>
                      {Object.entries(h.changes || {}).slice(0, 3).map(([k, v]) => (
                        <div key={k} className="truncate"><span className="text-slate-500">{k}:</span> {String(v?.from ?? "—")} → <span className="text-slate-700">{String(v?.to ?? "—")}</span></div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Center: Conversation */}
        <div className={`${isStaff ? "lg:col-span-6" : "lg:col-span-9"} space-y-4`}>
          {/* Attachments strip */}
          <div className="border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 flex items-center gap-2"><Paperclip className="h-3 w-3" /> Attachments · {attachments.length}</div>
              <input type="file" ref={fileRef} className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="attachment-input" />
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} className="border-slate-200 bg-transparent hover:bg-slate-100 rounded-sm text-xs" data-testid="attachment-upload-btn">
                <Paperclip className="h-3 w-3" /> Upload
              </Button>
            </div>
            {attachments.length === 0 ? (
              <div className="text-xs text-slate-500">Drop screenshots, logs, or documents (up to 5MB).</div>
            ) : (
              <div className="flex gap-2 flex-wrap">
                {attachments.map((a) => (
                  <a key={a.attachment_id} href={`${API_BASE}/attachments/${a.attachment_id}`} target="_blank" rel="noreferrer" className="border border-slate-200 bg-white px-3 py-2 rounded-sm text-xs hover:border-red-500 hover:text-red-700 transition-colors flex items-center gap-2" data-testid={`attachment-${a.attachment_id}`}>
                    <Paperclip className="h-3 w-3" strokeWidth={1.5} />
                    <span className="truncate max-w-[220px]">{a.filename}</span>
                    <span className="text-slate-500 font-mono">{Math.round(a.size / 1024)}KB</span>
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* CSAT */}
          {isReporter(ticket) && (ticket.status === "resolved" || ticket.status === "closed") && (
            <div className="border border-red-500/30 bg-red-500/5 p-5 rounded-sm">
              {ticket.csat_rating ? (
                <div className="flex items-center gap-3">
                  <div className="text-sm">You rated this ticket</div>
                  <div className="flex items-center gap-1">
                    {[1,2,3,4,5].map((n) => (
                      <Star key={n} className={`h-4 w-4 ${n <= ticket.csat_rating ? "text-amber-400 fill-amber-400" : "text-slate-700"}`} strokeWidth={1.5} />
                    ))}
                  </div>
                  {ticket.csat_comment && <div className="text-xs text-slate-600">&ldquo;{ticket.csat_comment}&rdquo;</div>}
                </div>
              ) : (
                <div>
                  <div className="font-display text-lg font-bold">How did we do?</div>
                  <div className="text-xs text-slate-600 mt-1">Rate your KINOS IT support experience.</div>
                  <div className="text-[10px] text-red-600/80 mt-1 font-mono uppercase tracking-[0.2em]" data-testid="csat-email-hint">Email survey also arrives ~1 hour after resolution</div>
                  <div className="flex items-center gap-1 mt-3">
                    {[1,2,3,4,5].map((n) => (
                      <button key={n} onClick={() => setCsatRating(n)} data-testid={`csat-star-${n}`} className="p-1">
                        <Star className={`h-6 w-6 transition-transform hover:scale-110 ${n <= csatRating ? "text-amber-400 fill-amber-400" : "text-slate-600"}`} strokeWidth={1.5} />
                      </button>
                    ))}
                  </div>
                  <Textarea value={csatComment} onChange={(e) => setCsatComment(e.target.value)} placeholder="Optional: tell us more..." rows={2} className="mt-3 bg-white border-slate-200 rounded-sm" data-testid="csat-comment" />
                  <Button onClick={submitCsat} className="mt-3 bg-red-600 hover:bg-red-500 rounded-sm" data-testid="csat-submit-btn">Submit rating</Button>
                </div>
              )}
            </div>
          )}

          {/* Conversation */}
          <div className="border border-slate-200 bg-white">
            <div className="p-4 border-b border-slate-200 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">Conversation · {comments.length}</div>
            <div className="divide-y divide-slate-800 max-h-[600px] overflow-auto">
              {comments.length === 0 && <div className="p-8 text-sm text-center text-slate-500">No replies yet.</div>}
              {comments.map((c) => {
                const detected = guessLang(c.content || "");
                const canTranslate = detected !== lang && (c.content || "").length > 3;
                const target = lang;
                const tr = translations[c.comment_id];
                const doTranslate = async () => {
                  if (tr?.text && tr.target === target) {
                    // Toggle off
                    setTranslations((p) => ({ ...p, [c.comment_id]: { ...tr, hidden: !tr.hidden } }));
                    return;
                  }
                  setTranslations((p) => ({ ...p, [c.comment_id]: { loading: true, target } }));
                  try {
                    const { data } = await api.post(`/translate`, { text: c.content, target });
                    setTranslations((p) => ({ ...p, [c.comment_id]: { text: data.translation, target, loading: false, hidden: false } }));
                  } catch {
                    toast.error("Translation failed");
                    setTranslations((p) => { const n = { ...p }; delete n[c.comment_id]; return n; });
                  }
                };
                return (
                <div key={c.comment_id} className={`p-5 flex gap-4 ${c.is_internal ? "bg-amber-500/5" : ""}`} data-testid={`comment-${c.comment_id}`}>
                  <Avatar className="h-9 w-9 rounded-sm">
                    <AvatarFallback className="rounded-sm bg-slate-200 text-xs">{c.author_name?.slice(0, 2).toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-xs flex-wrap">
                      <span className="font-semibold text-slate-900 inline-flex items-center gap-1">{c.author_name}<WinnerTrophy userId={c.author_id} /></span>
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{c.author_role?.replace("_", " ")}</span>
                      {c.is_internal && (<span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-amber-400"><Lock className="h-3 w-3" strokeWidth={1.5} /> Internal</span>)}
                      {c.time_spent_min > 0 && (<span className="inline-flex items-center gap-1 text-[10px] font-mono text-slate-600"><Clock className="h-3 w-3" strokeWidth={1.5} /> {c.time_spent_min}m</span>)}
                      <span className="text-slate-600 font-mono">·</span>
                      <span className="text-slate-500 font-mono">{new Date(c.created_at).toLocaleString()}</span>
                      {canTranslate && (
                        <button
                          type="button"
                          onClick={doTranslate}
                          data-testid={`translate-btn-${c.comment_id}`}
                          className="ml-auto inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.2em] text-red-600 hover:text-red-700 border border-red-200 hover:border-red-400 rounded-sm px-2 py-0.5 transition-colors"
                          title={target === "en" ? "Translate to English" : "Translate to Tetum"}
                        >
                          <Languages className="h-3 w-3" strokeWidth={1.75} />
                          {tr?.loading ? t("td_translating") : (tr?.text && !tr.hidden ? t("td_hide_translation") : t("td_translate"))}
                        </button>
                      )}
                    </div>
                    <div className="mt-2 text-sm text-slate-800 whitespace-pre-wrap leading-relaxed break-words">{c.content}</div>
                    {tr?.text && !tr.hidden && (
                      <div className="mt-3 border-l-2 border-red-400 pl-3 py-1 bg-red-50 rounded-sm" data-testid={`translation-${c.comment_id}`}>
                        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-red-700 mb-1">
                          {t("td_translation_label")} &middot; {target === "en" ? "EN" : "TET"}
                        </div>
                        <div className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed break-words">{tr.text}</div>
                      </div>
                    )}
                  </div>
                </div>
                );
              })}
            </div>

            {/* Reply composer */}
            <div className="p-5 border-t border-slate-200">
              {isStaff && (savedReplies.length > 0 || mySnippets.length > 0) && (
                <div className="mb-3 space-y-2">
                  {savedReplies.length > 0 && (() => {
                    const catName = ticket.category_name || "";
                    const matching = savedReplies.filter((r) => catName && (r.category || "").toLowerCase() === catName.toLowerCase());
                    const general = savedReplies.filter((r) => (r.category || "").toLowerCase() === "general");
                    const others = savedReplies.filter((r) => !matching.includes(r) && !general.includes(r));
                    return (
                      <>
                        {matching.length > 0 && (
                          <div className="flex items-center gap-2 flex-wrap" data-testid="category-replies-row">
                            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-400">{t("td_saved_for", { cat: catName })}</span>
                            {matching.slice(0, 8).map((r) => (
                              <ReplyChip key={r.reply_id} r={r} tone="match" onPick={setReply} />
                            ))}
                          </div>
                        )}
                        {(general.length > 0 || others.length > 0) && (
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">{t("td_team_replies")}</span>
                            {[...general, ...others].slice(0, 8).map((r) => (
                              <ReplyChip key={r.reply_id} r={r} tone="default" onPick={setReply} />
                            ))}
                          </div>
                        )}
                      </>
                    );
                  })()}
                  {mySnippets.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-red-600">{t("td_my_snippets")}</span>
                      {mySnippets.slice(0, 8).map((s) => (
                        <button key={s.snippet_id} onClick={() => setReply((prev) => prev ? `${prev}\n\n${s.content}` : s.content)} className="text-[11px] px-2 py-1 border border-red-500/40 bg-red-500/5 hover:border-cyan-400 rounded-sm text-red-700" data-testid={`snippet-${s.snippet_id}`}>
                          {s.title}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {isStaff ? (
                <div className="flex flex-col gap-4 relative" data-testid="composer-stack">
                  {mentionCtx && (() => {
                    const _norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
                    const key = _norm(mentionCtx.query);
                    const matches = users
                      .filter((u) => STAFF.includes(u.role) && u.user_id !== user?.user_id)
                      .filter((u) => !key || _norm(u.name).includes(key) || _norm((u.email || "").split("@")[0]).includes(key))
                      .slice(0, 6);
                    if (matches.length === 0) return null;
                    const isInternalField = mentionCtx.field === "internal";
                    return (
                      <div
                        data-testid="mention-suggest"
                        className={`absolute z-30 w-64 max-h-56 overflow-auto border border-red-500/40 bg-white/95 backdrop-blur rounded-sm shadow-xl ${isInternalField ? "left-3 bottom-8" : "left-3 top-40"}`}
                      >
                        <div className="px-3 py-2 border-b border-red-500/20 font-mono text-[10px] uppercase tracking-[0.2em] text-red-600">
                          {t("td_mention_teammate")}
                        </div>
                        {matches.map((u) => (
                          <button
                            key={u.user_id}
                            onMouseDown={(e) => { e.preventDefault(); insertMention(u.name); }}
                            data-testid={`mention-option-${u.user_id}`}
                            className="w-full text-left px-3 py-2 hover:bg-red-500/10 flex items-center gap-2 text-xs border-b border-slate-200 last:border-b-0"
                          >
                            <Avatar className="h-6 w-6 rounded-sm">
                              <AvatarFallback className="rounded-sm bg-cyan-900/60 text-[10px] text-cyan-200">{u.name.slice(0,2).toUpperCase()}</AvatarFallback>
                            </Avatar>
                            <div className="min-w-0 flex-1">
                              <div className="text-slate-900 truncate">{u.name}</div>
                              <div className="text-[10px] text-slate-500 uppercase tracking-[0.15em]">{u.role.replace("_"," ")}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                  {/* Internal note */}
                  <div className="border border-amber-500/30 bg-amber-500/5 rounded-sm p-3" data-testid="internal-note-box">
                    <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                      <div className="flex items-center gap-2">
                        <Lock className="h-3 w-3 text-amber-400" strokeWidth={1.5} />
                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-400">{t("td_internal_header")}</span>
                      </div>
                      {/* Internal-task macros: only shown for internal tickets */}
                      {ticket?.ticket_type === "internal" && (
                        <div className="flex items-center gap-1.5 flex-wrap" data-testid="internal-macro-row">
                          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500 mr-1"><Zap className="h-2.5 w-2.5 inline" strokeWidth={2} /> Macro:</span>
                          <button
                            type="button"
                            onClick={() => applyMacro("int_start")}
                            data-testid="macro-int-start-btn"
                            className="inline-flex items-center gap-1 px-2 h-6 text-[10px] font-mono uppercase tracking-[0.15em] border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-sm"
                          >Start</button>
                          <button
                            type="button"
                            onClick={() => applyMacro("int_progress")}
                            data-testid="macro-int-progress-btn"
                            className="inline-flex items-center gap-1 px-2 h-6 text-[10px] font-mono uppercase tracking-[0.15em] border border-cyan-300 bg-cyan-50 text-cyan-700 hover:bg-cyan-100 rounded-sm"
                          >Progress</button>
                          <button
                            type="button"
                            onClick={() => applyMacro("int_complete")}
                            data-testid="macro-int-complete-btn"
                            className="inline-flex items-center gap-1 px-2 h-6 text-[10px] font-mono uppercase tracking-[0.15em] border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded-sm"
                          >Complete</button>
                          <button
                            type="button"
                            onClick={() => applyMacro("int_blocked")}
                            data-testid="macro-int-blocked-btn"
                            className="inline-flex items-center gap-1 px-2 h-6 text-[10px] font-mono uppercase tracking-[0.15em] border border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100 rounded-sm"
                          >Blocked</button>
                        </div>
                      )}
                    </div>
                    <Textarea
                      data-testid="internal-textarea"
                      ref={internalRef}
                      value={internalNote}
                      onChange={onComposerInput("internal")}
                      onKeyUp={(e) => setMentionCtx(detectMention(e.target, "internal"))}
                      onBlur={() => setTimeout(() => setMentionCtx((c) => (c && c.field === "internal" ? null : c)), 150)}
                      placeholder={t("td_internal_placeholder")}
                      rows={10}
                      className="bg-white border-amber-500/20 rounded-sm text-sm min-h-[200px] max-h-[70vh] resize-y overflow-y-auto"
                    />
                    <div className="text-[10px] font-mono text-slate-500 mt-1">{t("td_internal_hint")}</div>
                  </div>
                  {/* Public reply */}
                  <div className="order-first border border-red-500/30 bg-red-500/5 rounded-sm p-3" data-testid="public-reply-box">
                    <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                      <div className="flex items-center gap-2">
                        <Sparkles className="h-3 w-3 text-red-600" strokeWidth={1.5} />
                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-red-600">{t("td_public_header")}</span>
                      </div>
                      {/* Macros — one-tap templates for the common outcomes */}
                      <div className="flex items-center gap-1.5" data-testid="macro-row">
                        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500 mr-1"><Zap className="h-2.5 w-2.5 inline" strokeWidth={2} /> Macro:</span>
                        <button
                          type="button"
                          onClick={() => applyMacro("solve")}
                          data-testid="macro-solve-btn"
                          className="inline-flex items-center gap-1 px-2 h-6 text-[10px] font-mono uppercase tracking-[0.15em] border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded-sm"
                        >Solve</button>
                        <button
                          type="button"
                          onClick={() => applyMacro("pending")}
                          data-testid="macro-pending-btn"
                          className="inline-flex items-center gap-1 px-2 h-6 text-[10px] font-mono uppercase tracking-[0.15em] border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 rounded-sm"
                        >Pending</button>
                        <button
                          type="button"
                          onClick={() => applyMacro("follow")}
                          data-testid="macro-follow-btn"
                          className="inline-flex items-center gap-1 px-2 h-6 text-[10px] font-mono uppercase tracking-[0.15em] border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-sm"
                        >Follow-up</button>
                      </div>
                    </div>
                    <Textarea
                      data-testid="reply-textarea"
                      ref={replyRef}
                      value={reply}
                      onChange={onComposerInput("reply")}
                      onKeyUp={(e) => setMentionCtx(detectMention(e.target, "reply"))}
                      onBlur={() => setTimeout(() => setMentionCtx((c) => (c && c.field === "reply" ? null : c)), 150)}
                      placeholder={t("td_public_placeholder")}
                      rows={10}
                      className={`bg-white border-red-500/20 rounded-sm text-sm min-h-[200px] max-h-[70vh] resize-y overflow-y-auto ${drafting ? "shimmer" : ""}`}
                    />
                    <label className="mt-2 flex items-center gap-2 text-xs text-slate-700 cursor-pointer" title={`Send this reply to ${ticket.reporter_email}`}>
                      <input type="checkbox" checked={emailCustomer} onChange={(e) => setEmailCustomer(e.target.checked)} data-testid="email-customer-checkbox" />
                      <span>{t("td_email_customer", { email: "" }).replace("{email}", "")}<span className="text-red-700 font-mono break-all">{ticket.reporter_email}</span> <span className="text-slate-500">{t("td_email_default")}</span></span>
                    </label>
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">After post →</span>
                      <Select value={nextStatus} onValueChange={setNextStatus}>
                        <SelectTrigger className="h-7 w-52 bg-white border-red-500/30 rounded-sm text-xs" data-testid="next-status-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending_customer" data-testid="next-status-pending">Waiting on customer</SelectItem>
                          <SelectItem value="in_progress" data-testid="next-status-inprogress">Still working (In progress)</SelectItem>
                          <SelectItem value="pending_third_party" data-testid="next-status-third">Waiting on 3rd party</SelectItem>
                          <SelectItem value="resolved" data-testid="next-status-resolved">Mark as resolved</SelectItem>
                          <SelectItem value="__keep__" data-testid="next-status-keep">Keep current status</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              ) : (
                <Textarea
                  data-testid="reply-textarea"
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Add context or a follow-up..."
                  rows={4}
                  className="bg-white border-slate-200 rounded-sm"
                />
              )}

              <div className="mt-3 flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-4 flex-wrap">
                  {isStaff && (
                    <>
                      <label className="flex items-center gap-2 text-xs text-slate-600">
                        <Clock className="h-3 w-3" strokeWidth={1.5} /> {t("td_time_min")}
                        <Input type="number" value={timeSpent} onChange={(e) => setTimeSpent(e.target.value)} className="h-7 w-16 bg-white border-slate-200 rounded-sm text-xs" data-testid="time-spent-input" />
                      </label>
                      <Button variant="ghost" size="sm" onClick={draft} disabled={drafting} data-testid="ai-draft-btn" className="text-red-600 hover:text-red-700 hover:bg-red-500/10 rounded-sm">
                        <Sparkles className="h-4 w-4" strokeWidth={1.5} /> {drafting ? t("td_ai_drafting") : t("td_ai_draft")}
                      </Button>
                    </>
                  )}
                </div>
                <Button onClick={submitReply} className="bg-red-600 hover:bg-red-500 rounded-sm" data-testid="post-reply-btn">
                  {isStaff && internalNote.trim() && reply.trim()
                    ? t("td_post_log_and_email")
                    : isStaff && internalNote.trim()
                    ? t("td_post_log")
                    : isStaff && reply.trim() && emailCustomer
                    ? t("td_post_and_email")
                    : t("td_post")}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Customer & Asset info */}
        <div className="lg:col-span-3 space-y-4">
          <div className="border border-slate-200 bg-white p-4 space-y-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">Customer</div>
            <InfoRow icon={<Avatar className="h-6 w-6 rounded-sm"><AvatarFallback className="rounded-sm bg-slate-200 text-[10px]">{ticket.reporter_name?.slice(0,2).toUpperCase()}</AvatarFallback></Avatar>} label={ticket.reporter_name} value={ticket.reporter_email} />
            <InfoRow icon={<Phone className="h-3 w-3 text-slate-500" strokeWidth={1.5} />} label="Phone" value={ticket.reporter_phone || "—"} />
            <InfoRow icon={<Building2 className="h-3 w-3 text-slate-500" strokeWidth={1.5} />} label="Organization" value={ticket.organization || "—"} editable={canEditReporterFields} onSave={(v) => update({ organization: v })} testid="edit-organization" />
            <InfoRow label="Department" value={ticket.department || "—"} editable={canEditReporterFields} onSave={(v) => update({ department: v })} testid="edit-department" />
            <InfoRow icon={<MapPin className="h-3 w-3 text-slate-500" strokeWidth={1.5} />} label="Location" value={ticket.location || "—"} editable={canEditReporterFields} onSave={(v) => update({ location: v })} testid="edit-location" />
          </div>

          <div className="border border-slate-200 bg-white p-4 space-y-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 flex items-center gap-2"><Cpu className="h-3 w-3" /> Asset</div>
            <InfoRow label="Asset tag" value={ticket.asset_tag || "—"} editable={canEditReporterFields} onSave={(v) => update({ asset_tag: v })} testid="edit-asset" />
          </div>

          <div className="border border-slate-200 bg-white p-4 space-y-2 text-xs">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-2">SLA</div>
            <div className="flex justify-between"><span className="text-slate-500">First response due</span><span className="font-mono text-slate-700">{ticket.first_response_due_at ? new Date(ticket.first_response_due_at).toLocaleString() : "—"}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">First response at</span><span className="font-mono text-slate-700">{ticket.first_response_at ? new Date(ticket.first_response_at).toLocaleString() : "pending"}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Resolution due</span><span className="font-mono text-slate-700">{ticket.resolution_due_at ? new Date(ticket.resolution_due_at).toLocaleString() : "—"}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Resolved at</span><span className="font-mono text-slate-700">{ticket.resolved_at ? new Date(ticket.resolved_at).toLocaleString() : "—"}</span></div>
          </div>

          {ticket.csat_rating && (
            <div className="border border-emerald-500/30 bg-emerald-500/5 p-4 rounded-sm">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-400 mb-2">Customer rating</div>
              <div className="flex items-center gap-1">
                {[1,2,3,4,5].map((n) => (<Star key={n} className={`h-4 w-4 ${n <= ticket.csat_rating ? "text-amber-400 fill-amber-400" : "text-slate-700"}`} strokeWidth={1.5} />))}
              </div>
              {ticket.csat_comment && <div className="mt-2 text-xs text-slate-600 italic">&ldquo;{ticket.csat_comment}&rdquo;</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ReplyChip({ r, tone, onPick }) {
  return (
    <button
      onClick={() => onPick((prev) => (prev ? `${prev}\n\n${r.content}` : r.content))}
      className={`text-[11px] px-2 py-1 border rounded-sm ${
        tone === "match"
          ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-200 hover:border-emerald-400"
          : "border-slate-200 hover:border-cyan-500 text-slate-700 hover:text-red-700"
      }`}
      data-testid={`saved-reply-${r.reply_id}`}
    >
      {r.title}
    </button>
  );
}

function SB({ label, children }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1.5">{label}</div>
      {children}
    </div>
  );
}

function InfoRow({ icon, label, value, editable, onSave, testid }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value === "—" ? "" : value);
  useEffect(() => { setVal(value === "—" ? "" : value); }, [value]);

  if (editing) {
    return (
      <div className="flex items-center gap-2">
        <Input value={val} onChange={(e) => setVal(e.target.value)} className="h-8 bg-white border-slate-200 rounded-sm text-xs" data-testid={testid} />
        <Button size="sm" className="h-8 bg-red-600 hover:bg-red-500 rounded-sm text-xs px-2" onClick={() => { onSave(val); setEditing(false); }} data-testid={`${testid}-save`}>Save</Button>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2 text-xs ${editable ? "cursor-pointer hover:bg-slate-100 -mx-2 px-2 py-1 rounded-sm" : ""}`} onClick={() => editable && setEditing(true)}>
      {icon}
      <div className="flex-1 min-w-0">
        <div className="text-slate-500 text-[10px] font-mono uppercase tracking-wider">{label}</div>
        <div className="text-slate-800 truncate">{value}</div>
      </div>
    </div>
  );
}
